import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { getBackendUrl } from "@src/utils/getBackendUrl";
import { getAuthHeaders } from "@src/utils/getAuthHeader";
import { PipelineHealthSummary } from "./pipelineHealth.types";

type Status = "loading" | "loaded" | "error";

interface UsePipelineHealthSummaryResult {
	summary: PipelineHealthSummary | null;
	loading: boolean;
	error: boolean;
	refreshing: boolean;
	refetch: () => void;
}

const POLL_INTERVAL_MS = 600000;

export const usePipelineHealthSummary = (): UsePipelineHealthSummaryResult => {
	const [summary, setSummary] = useState<PipelineHealthSummary | null>(null);
	const [status, setStatus] = useState<Status>("loading");
	const [refreshing, setRefreshing] = useState(false);
	const requestIdRef = useRef(0);
	const manualRequestIdRef = useRef(0);
	const controllersRef = useRef<Set<AbortController>>(new Set());

	const fetchSummary = useCallback((isManual: boolean) => {
		const requestId = ++requestIdRef.current;
		const manualRequestId = isManual ? ++manualRequestIdRef.current : null;
		if (isManual) setRefreshing(true);

		const controller = new AbortController();
		controllersRef.current.add(controller);

		axios
			.get<PipelineHealthSummary>(`${getBackendUrl()}/pipeline-health`, { headers: getAuthHeaders(), signal: controller.signal })
			.then((response) => {
				if (requestIdRef.current !== requestId) return;
				setSummary(response.data);
				setStatus("loaded");
			})
			.catch((err) => {
				if (axios.isCancel(err)) return;
				if (requestIdRef.current !== requestId) return;
				setStatus("error");
			})
			.finally(() => {
				controllersRef.current.delete(controller);
				if (manualRequestId != null && manualRequestIdRef.current === manualRequestId) {
					setRefreshing(false);
				}
			});
	}, []);

	useEffect(() => {
		const controllers = controllersRef.current;
		fetchSummary(false);
		const interval = setInterval(() => fetchSummary(false), POLL_INTERVAL_MS);
		return () => {
			clearInterval(interval);
			controllers.forEach((controller) => controller.abort());
			controllers.clear();
		};
	}, [fetchSummary]);

	const refetch = useCallback(() => fetchSummary(true), [fetchSummary]);

	return { summary, loading: status === "loading", error: status === "error", refreshing, refetch };
};
